import React, { useState, useRef, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocalizedPath, useCurrentLanguage } from 'utils/localizedRouting';
import { SUPPORTED_LANG_CODES } from 'i18n/config';
import { GlobeIcon, ChevronDownIcon, CheckIcon } from 'components/icons';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { updateUserLanguage } from 'store/authSlice';
import { authService } from 'api/auth';
import styles from './LanguageDropdown.module.scss';

export interface LanguageOption {
  code: string;
  label: string;
  flag?: string;
  logoUrl?: string;
}

export const DEFAULT_LANGUAGE_FLAGS: Record<string, string> = {
  en: '🇺🇸',
  ru: '🇷🇺',
  zh: '🇨🇳',
  ko: '🇰🇷',
  es: '🇪🇸',
  pt: '🇧🇷',
  tr: '🇹🇷',
  de: '🇩🇪',
  fr: '🇫🇷',
  hy: '🇦🇲',
  hi: '🇮🇳',
};

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'hy', label: 'Հայերեն', flag: '🇦🇲' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
];

export interface LanguageDropdownProps {
  value?: string;
  onChange?: (code: string) => void;
  direction?: 'up' | 'down';
  className?: string;
  options?: LanguageOption[];
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  value,
  onChange,
  direction = 'up',
  className,
  options,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { lang: urlLang } = useParams<{ lang?: string }>();
  const currentLang = useCurrentLanguage();
  const { i18n } = useTranslation();

  const { languages: siteLanguages, hasFetchedLanguages } = useAppSelector((state) => state.site);
  const isAuthenticated = useAppSelector((state) => state.auth?.isAuthenticated);

  const availableOptions: LanguageOption[] = useMemo(() => {
    if (options && options.length > 0) return options;
    if (siteLanguages && siteLanguages.length > 0) {
      return siteLanguages
        .filter((lang) => (SUPPORTED_LANG_CODES as readonly string[]).includes(lang.code))
        .map((lang) => ({
          code: lang.code,
          label: lang.nativeName || lang.name,
          flag: DEFAULT_LANGUAGE_FLAGS[lang.code] || '🌐',
          logoUrl: lang.logoUrl || undefined,
        }));
    }
    return SUPPORTED_LANGUAGES;
  }, [options, siteLanguages]);

  // Hide the dropdown if only 1 language is available (e.g. backend enabled only English)
  const shouldHideDropdown = useMemo(() => {
    if (options && options.length > 0) {
      return options.length <= 1;
    }
    if (hasFetchedLanguages) {
      return availableOptions.length <= 1;
    }
    // While backend languages are still loading on startup, do not render to avoid UI flash
    return true;
  }, [options, hasFetchedLanguages, availableOptions]);

  // Controlled vs uncontrolled state pattern without redundant effect sync
  const isControlled = value !== undefined;
  const [uncontrolledLang, setUncontrolledLang] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('language') || 'en';
    }
    return 'en';
  });

  const activeCode = isControlled ? value : urlLang || currentLang || uncontrolledLang;
  const currentOption =
    availableOptions.find((opt) => opt.code === activeCode) ||
    availableOptions[0] ||
    SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (code: string) => {
    if (code === activeCode) {
      setIsOpen(false);
      return;
    }

    if (!isControlled) {
      setUncontrolledLang(code);
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', code);
    }
    onChange?.(code);
    setIsOpen(false);

    // Switch i18n active locale
    i18n.changeLanguage(code);

    // Update URL route using prefix-except-default helper
    const targetPath = getLocalizedPath(location.pathname + location.search + location.hash, code);
    navigate(targetPath, { state: location.state });

    // Update Redux user language state
    dispatch(updateUserLanguage(code));

    // Silent background sync if authenticated
    if (isAuthenticated) {
      authService.updateLanguage(code).catch((err) => {
        console.error('Failed to update language on profile:', err);
      });
    }
  };

  const renderFlag = (opt: LanguageOption) => {
    if (opt.logoUrl) {
      return (
        <img src={opt.logoUrl} alt={opt.label} className={styles.flagImg} width={18} height={13} />
      );
    }
    if (opt.flag) {
      return <span className={styles.flag}>{opt.flag}</span>;
    }
    return null;
  };

  if (shouldHideDropdown) {
    return null;
  }

  return (
    <div className={cx(styles.languageDropdownWrapper, className)} ref={dropdownRef}>
      <button
        type="button"
        className={cx(styles.triggerButton, { [styles.open]: isOpen })}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={styles.globeIcon}>
          <GlobeIcon width={16} height={16} />
        </span>
        <span className={styles.label}>
          {renderFlag(currentOption)}
          <span>{currentOption.label}</span>
        </span>
        <span className={cx(styles.chevron, { [styles.open]: isOpen })}>
          <ChevronDownIcon width={14} height={14} />
        </span>
      </button>

      {isOpen && (
        <div
          className={cx(styles.menu, {
            [styles.directionUp]: direction === 'up',
            [styles.directionDown]: direction === 'down',
          })}
          role="listbox"
        >
          {availableOptions.map((option) => {
            const isSelected = option.code === activeCode;
            return (
              <button
                key={option.code}
                type="button"
                className={cx(styles.optionItem, { [styles.active]: isSelected })}
                onClick={() => handleSelect(option.code)}
                role="option"
                aria-selected={isSelected}
              >
                <div className={styles.optionContent}>
                  {renderFlag(option)}
                  <span>{option.label}</span>
                </div>
                {isSelected && (
                  <span className={styles.checkIndicator}>
                    <CheckIcon width={14} height={14} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
